import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Heart, Loader2, Eye, EyeOff, Mail, Lock, UserPlus } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card'
import { portalAuthApi } from '@services/portalAuth.service'
import { usePortalAuthStore } from '@hooks/usePortalAuthStore'
import { useToast } from '@hooks/use-toast'
import { cn } from '@lib/utils'

export function PortalLoginPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const login = usePortalAuthStore((state) => state.login)

  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await portalAuthApi.loginJson({
        email: formData.email,
        password: formData.password,
      })

      login(response)

      toast({
        title: 'Bienvenido al portal del paciente',
        description: `Hola, ${response.user.first_name}!`,
      })

      navigate('/portal/dashboard')
    } catch (error) {
      toast({
        title: 'No se pudo iniciar sesión',
        description: 'Correo o contraseña inválidos. Inténtalo de nuevo.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-medical-50 via-white to-medical-100 dark:from-slate-950 dark:via-slate-900 dark:to-medical-950 p-4">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-b from-medical-100/50 to-transparent rounded-full blur-3xl dark:from-medical-900/20" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-t from-blue-100/50 to-transparent rounded-full blur-3xl dark:from-blue-900/20" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="flex flex-col items-center mb-8">
          <div className="h-20 w-20 rounded-2xl gradient-medical flex items-center justify-center shadow-2xl shadow-medical-500/30 mb-4">
            <Heart className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100 tracking-tight">
            Portal del paciente
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Accede a tu información de salud y gestiona tus citas
          </p>
        </div>

        <Card className="shadow-2xl border-medical-100 dark:border-medical-800">
          <CardHeader className="space-y-1 text-center pb-6">
            <CardTitle className="text-xl font-semibold">
              Inicia sesión en tu cuenta
            </CardTitle>
            <CardDescription>
              Ingresa tus credenciales para acceder al portal del paciente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Correo electrónico
                </Label>
                <div className="relative">
                  <Mail
                    className={cn(
                      "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors",
                      focusedField === 'email' ? "text-medical-600" : "text-muted-foreground"
                    )}
                  />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    className="pl-10 h-11"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Contraseña
                  </Label>
                  <Link
                    to="/portal/forgot-password"
                    className="text-xs text-medical-600 hover:text-medical-700 dark:text-medical-400"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
                <div className="relative">
                  <Lock
                    className={cn(
                      "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors",
                      focusedField === 'password' ? "text-medical-600" : "text-muted-foreground"
                    )}
                  />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    className="pl-10 pr-10 h-11"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 gradient-medical hover:opacity-90 transition-all shadow-lg shadow-medical-500/25 text-base font-medium"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Iniciando sesión...
                  </>
                ) : (
                  'Iniciar sesión'
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-border">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  ¿No tienes cuenta?{' '}
                  <Link
                    to="/portal/register"
                    className="text-medical-600 hover:text-medical-700 font-medium inline-flex items-center gap-1"
                  >
                    <UserPlus className="h-3 w-3" />
                    Regístrate ahora
                  </Link>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            <Link to="/login" className="text-medical-600 hover:text-medical-700">
              Acceso para personal →
            </Link>
          </p>
          <p className="text-xs text-muted-foreground mt-4">
            © 2025 MediCore HMS. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  )
}
